import React, { useState, useEffect, useRef } from "react";

interface AiAnswerData {
  question: string;
  answer: string;
  searchResults?: Array<any>;
}

interface AiSuggestionBoxProps {
  content: string | AiAnswerData;
  isLoading: boolean;
  searchQuery: string;
}

function AiSuggestionBox({
  content,
  isLoading,
  searchQuery,
}: AiSuggestionBoxProps) {
  const [streamedText, setStreamedText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const streamIndexRef = useRef(0);
  const streamTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset streaming khi có content mới
  useEffect(() => {
    if (!isLoading && content) {
      setIsStreaming(true);
      setStreamedText("");
      streamIndexRef.current = 0;
    }
    return () => {
      if (streamTimeoutRef.current) {
        clearTimeout(streamTimeoutRef.current);
      }
    };
  }, [content, isLoading]);

  // Stream text effect
  useEffect(() => {
    if (!isLoading && content && isStreaming) {
      let answerData: AiAnswerData | null = null;
      if (typeof content === "string") {
        try {
          const parsed = JSON.parse(content);
          if (parsed.answer) {
            answerData = parsed;
          }
        } catch {
          // Nếu không parse được, coi như string thuần
        }
      } else if (typeof content === "object" && (content as any).answer) {
        answerData = content as AiAnswerData;
      }

      if (!answerData?.answer) {
        setIsStreaming(false);
        return;
      }

      const fullText = answerData.answer;

      // Chia text thành các từ để stream tự nhiên hơn
      const words = fullText.split(/(\s+)/);
      let currentWordIndex = 0;

      const streamNext = () => {
        if (currentWordIndex < words.length) {
          // Stream từng từ hoặc vài từ một lúc để nhanh hơn
          const wordsToAdd = Math.min(
            currentWordIndex + Math.floor(Math.random() * 2) + 1,
            words.length
          );
          const newText = words.slice(0, wordsToAdd).join("");
          setStreamedText(newText);
          currentWordIndex = wordsToAdd;
          streamIndexRef.current = newText.length;

          // Delay ngẫu nhiên 30-80ms để tạo hiệu ứng tự nhiên
          const delay = Math.random() * 50 + 30;
          streamTimeoutRef.current = setTimeout(streamNext, delay);
        } else {
          setIsStreaming(false);
        }
      };

      streamNext();

      return () => {
        if (streamTimeoutRef.current) {
          clearTimeout(streamTimeoutRef.current);
        }
      };
    }
  }, [content, isLoading, isStreaming]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-5 md:p-6 border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <div className="relative">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <span className="text-[#1677ff] font-semibold text-sm sm:text-base md:text-lg">
            Thông tin tổng quan bằng AI:
          </span>
        </div>
        <div className="flex items-center gap-2 text-gray-600 text-sm sm:text-base">
          <span className="animate-pulse">Đang suy nghĩ</span>
          <span className="flex gap-1">
            <span className="animate-bounce" style={{ animationDelay: "0ms" }}>
              .
            </span>
            <span
              className="animate-bounce"
              style={{ animationDelay: "150ms" }}
            >
              .
            </span>
            <span
              className="animate-bounce"
              style={{ animationDelay: "300ms" }}
            >
              .
            </span>
          </span>
        </div>
      </div>
    );
  }

  if (!content) {
    return null;
  }

  // Parse content - có thể là string hoặc object
  let answerData: AiAnswerData | null = null;
  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);
      if (parsed.answer) {
        answerData = parsed;
      }
    } catch {
      // Nếu không parse được, coi như string thuần
    }
  } else if (typeof content === "object" && (content as any).answer) {
    answerData = content as AiAnswerData;
  }

  if (!answerData || !answerData.answer) {
    return null;
  }

  // Sử dụng streamedText nếu đang streaming, nếu không dùng full text
  const textToFormat = isStreaming ? streamedText : answerData.answer;

  // Parse markdown để format
  const formatMarkdown = (text: string): string => {
    let formatted = text;

    // Split by paragraphs first
    const paragraphs = formatted.split(/\n\n/);

    let result = "";

    paragraphs.forEach((para) => {
      if (!para.trim()) return;

      // Numbered list item với bold title (1. **Title**:)
      if (/^\d+\.\s+\*\*/.test(para)) {
        const match = para.match(/^(\d+)\.\s+\*\*(.+?)\*\*:/);
        if (match) {
          result += `<div class="mb-3"><span class="font-bold text-gray-900">${match[1]}. ${match[2]}:</span>`;
          const rest = para.replace(/^\d+\.\s+\*\*.+?\*\*:\s*/, "");
          if (rest.trim()) {
            // Parse bullet points trong phần còn lại
            const bullets = rest
              .split(/\n/)
              .filter((line) => line.trim().startsWith("-"));
            const nonBullets = rest
              .split(/\n/)
              .filter((line) => !line.trim().startsWith("-"));

            if (nonBullets.join("").trim()) {
              result += ` <span>${formatInlineText(nonBullets.join(" "))}</span>`;
            }

            if (bullets.length > 0) {
              result += "<ul class='ml-6 mt-2 space-y-1'>";
              bullets.forEach((bullet) => {
                const bulletText = bullet.replace(/^-\s+/, "");
                result += `<li class="flex"><span class="mr-2">•</span><span>${formatInlineText(bulletText)}</span></li>`;
              });
              result += "</ul>";
            }
          }
          result += "</div>";
        }
      }
      // Regular paragraph
      else {
        result += `<div class="mb-3">${formatInlineText(para)}</div>`;
      }
    });

    return result;
  };

  // Format inline text (bold, điều luật, số phần trăm)
  const formatInlineText = (text: string): string => {
    let formatted = text;

    // Bold **text**
    formatted = formatted.replace(
      /\*\*(.+?)\*\*/g,
      '<strong class="font-bold text-gray-900">$1</strong>'
    );

    // Highlight điều luật (Điều 134)
    formatted = formatted.replace(
      /(Điều \d+[^.]*)/g,
      '<strong class="font-bold text-gray-900">$1</strong>'
    );

    // Highlight số phần trăm và số năm (11%, 03 năm, etc.)
    formatted = formatted.replace(
      /(\d+%)/g,
      '<strong class="font-bold text-gray-900">$1</strong>'
    );

    // Convert single line breaks
    formatted = formatted.replace(/\n/g, " ");

    return formatted;
  };

  const formattedAnswer = formatMarkdown(textToFormat);

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-5 md:p-6 border border-gray-200">
      <div className="flex items-center gap-2 mb-4 md:mb-5">
        <img
          src="/images/bot_tab@3x.png"
          style={{ width: 45, height: 45 }}
          alt=""
        />
        <span className="text-[#1677ff] font-semibold text-sm sm:text-base md:text-lg">
          Thông tin tổng quan bằng AI:
        </span>
      </div>

      <div
        className="text-sm sm:text-base md:text-lg text-gray-800 leading-relaxed"
        dangerouslySetInnerHTML={{
          __html: formattedAnswer,
        }}
      />
    </div>
  );
}

export default AiSuggestionBox;
